import { Link } from "react-router-dom";
import { Phone, ShieldCheck, FileText, Lock, Mail } from "lucide-react";
import { Overline } from "@/components/Section";

const Section = ({ title, children, id }) => (
    <section id={id} className="scroll-mt-24" data-testid={`mentions-section-${id}`}>
        <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight text-[#0A0A0A]">
            {title}
        </h2>
        <div className="mt-5 space-y-4 text-zinc-700 text-sm sm:text-base leading-relaxed">
            {children}
        </div>
    </section>
);

const Row = ({ k, v }) => (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-2 border-b border-black/10 last:border-b-0">
        <div className="sm:w-56 text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">{k}</div>
        <div className="text-zinc-800">{v}</div>
    </div>
);

const TOC = [
    { id: "editeur", label: "Éditeur du site" },
    { id: "hebergeur", label: "Hébergeur" },
    { id: "propriete", label: "Propriété intellectuelle" },
    { id: "responsabilite", label: "Limitation de responsabilité" },
    { id: "rgpd", label: "Données personnelles (RGPD)" },
    { id: "cookies", label: "Cookies" },
    { id: "droits", label: "Vos droits" },
    { id: "contact", label: "Contact" },
];

const MentionsLegales = () => {
    const today = new Date().toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });

    return (
        <>
            {/* HERO */}
            <section className="relative border-b border-black/10 grid-bg" data-testid="mentions-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 lg:pt-20 pb-14 lg:pb-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-9 fade-up fade-up-1">
                            <Overline className="mb-5">◉ Informations légales</Overline>
                            <h1 className="font-display font-bold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                                Mentions<br />légales <span className="text-[#E63946]">& RGPD.</span>
                            </h1>
                            <p className="mt-7 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-2xl">
                                Informations relatives à l'éditeur du site, à l'hébergement, aux conditions d'utilisation et
                                au traitement des données personnelles dans le cadre du Règlement Général sur la Protection des Données.
                            </p>
                            <div className="mt-6 text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">
                                Dernière mise à jour · {today}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24 bg-white border-b border-black/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* TOC */}
                    <aside className="lg:col-span-3" data-testid="mentions-toc">
                        <div className="lg:sticky lg:top-24">
                            <Overline className="mb-4">◉ Sommaire</Overline>
                            <nav className="border-l border-black/10">
                                {TOC.map((item) => (
                                    <a
                                        key={item.id}
                                        href={`#${item.id}`}
                                        className="block px-4 py-2.5 text-sm text-zinc-600 hover:text-[#0A0A0A] hover:border-l-2 hover:border-[#E63946] hover:-ml-px transition-all"
                                        data-testid={`mentions-toc-${item.id}`}
                                    >
                                        {item.label}
                                    </a>
                                ))}
                            </nav>
                            <a
                                href="tel:+33649501843"
                                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.15em] hover:text-[#E63946] transition-colors"
                                data-testid="mentions-call-link"
                            >
                                <Phone className="h-4 w-4" /> 06 49 50 18 43
                            </a>
                        </div>
                    </aside>

                    {/* CONTENT */}
                    <div className="lg:col-span-9 space-y-14">
                        <Section id="editeur" title="Éditeur du site">
                            <p>
                                Le présent site est édité par <strong>Atelier Beauchamp</strong>, artisan serrurier indépendant
                                intervenant 24h/24 et 7j/7 dans la commune.
                            </p>
                            <div className="mt-4 border border-black/10 bg-white p-5 sm:p-7">
                                <Row k="Dénomination" v="Atelier Beauchamp" />
                                <Row k="Activité" v="Serrurerie, dépannage, pose de porte blindée" />
                                <Row k="Téléphone" v={<a href="tel:+33649501843" className="font-semibold underline underline-offset-2 hover:text-[#E63946]">06 49 50 18 43</a>} />
                                <Row k="Zone d'intervention" v="Plusieurs communes de France" />
                                <Row k="Directeur de la publication" v="Le gérant d'Atelier Beauchamp" />
                            </div>
                            <p className="text-xs text-zinc-500">
                                Numéro SIRET, forme juridique et adresse postale : communiqués sur demande au 06 49 50 18 43.
                            </p>
                        </Section>

                        <Section id="hebergeur" title="Hébergeur">
                            <p>
                                Le site est hébergé sur l'infrastructure <strong>Emergent</strong> (emergent.sh).
                                Le contenu est servi via une infrastructure cloud située dans l'Union Européenne ou aux États-Unis,
                                conformément aux conditions de l'hébergeur.
                            </p>
                        </Section>

                        <Section id="propriete" title="Propriété intellectuelle">
                            <p>
                                L'ensemble des éléments composant le site (textes, logos, images, photographies, structure, code source,
                                graphismes, mise en page) sont la propriété exclusive d'Atelier Beauchamp ou de leurs auteurs respectifs,
                                et sont protégés par le droit d'auteur et le droit des marques.
                            </p>
                            <p>
                                Toute reproduction, représentation, modification ou exploitation, totale ou partielle, sans autorisation
                                écrite préalable, est strictement interdite et constitue une contrefaçon sanctionnée par les articles
                                L.335-2 et suivants du Code de la propriété intellectuelle.
                            </p>
                            <p className="text-xs text-zinc-500">
                                Crédits photos : Pexels, Unsplash (licences libres d'utilisation).
                            </p>
                        </Section>

                        <Section id="responsabilite" title="Limitation de responsabilité">
                            <p>
                                Atelier Beauchamp s'efforce de fournir des informations exactes et tenues à jour. Toutefois, des erreurs
                                ou omissions peuvent survenir. L'utilisateur est invité à signaler toute inexactitude par téléphone au
                                06 49 50 18 43.
                            </p>
                            <p>
                                Atelier Beauchamp ne saurait être tenu responsable de l'utilisation faite des informations diffusées sur
                                le site, ni de tout dommage direct ou indirect résultant de la consultation ou de l'utilisation du site,
                                de l'impossibilité d'y accéder, ou de la fiabilité des transmissions.
                            </p>
                        </Section>

                        <Section id="rgpd" title="Données personnelles (RGPD)">
                            <p>
                                Conformément au Règlement Général sur la Protection des Données (Règlement UE 2016/679, dit « RGPD ») et
                                à la loi « Informatique et Libertés » du 6 janvier 1978 modifiée, l'utilisateur dispose de droits sur ses
                                données personnelles.
                            </p>

                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="border border-black/10 bg-white p-5">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 grid place-items-center bg-[#0A0A0A] text-white">
                                            <FileText className="h-4 w-4" strokeWidth={2.4} />
                                        </div>
                                        <h3 className="font-display font-bold uppercase tracking-tight">Données collectées</h3>
                                    </div>
                                    <ul className="mt-4 space-y-1.5 text-sm text-zinc-700">
                                        <li>· Nom (formulaire de contact)</li>
                                        <li>· Téléphone (formulaire de contact)</li>
                                        <li>· Ville (optionnel)</li>
                                        <li>· Service souhaité (optionnel)</li>
                                        <li>· Message (optionnel)</li>
                                    </ul>
                                </div>

                                <div className="border border-black/10 bg-white p-5">
                                    <div className="flex items-center gap-3">
                                        <div className="h-10 w-10 grid place-items-center bg-[#0A0A0A] text-white">
                                            <ShieldCheck className="h-4 w-4" strokeWidth={2.4} />
                                        </div>
                                        <h3 className="font-display font-bold uppercase tracking-tight">Finalités</h3>
                                    </div>
                                    <ul className="mt-4 space-y-1.5 text-sm text-zinc-700">
                                        <li>· Répondre à votre demande de devis</li>
                                        <li>· Vous rappeler pour préciser votre besoin</li>
                                        <li>· Établir et exécuter une prestation</li>
                                        <li>· Assurer le suivi commercial</li>
                                    </ul>
                                </div>
                            </div>

                            <p className="mt-4">
                                <strong>Base légale</strong> : exécution de mesures précontractuelles à votre demande (article 6.1.b du RGPD)
                                et intérêt légitime de l'éditeur à répondre aux sollicitations entrantes.
                            </p>
                            <p>
                                <strong>Durée de conservation</strong> : les données issues du formulaire de contact sont conservées pendant
                                <strong> 3 ans</strong> à compter du dernier contact, à des fins de prospection commerciale, puis archivées
                                ou supprimées conformément aux obligations légales (notamment comptables : 10 ans).
                            </p>
                            <p>
                                <strong>Destinataires</strong> : les données sont traitées par Atelier Beauchamp uniquement.
                                Elles ne sont ni vendues, ni cédées, ni transmises à des tiers à des fins commerciales.
                            </p>
                            <p>
                                <strong>Sécurité</strong> : les données sont stockées sur une base de données sécurisée (MongoDB),
                                accessible uniquement par l'éditeur via un canal chiffré (HTTPS).
                            </p>
                        </Section>

                        <Section id="cookies" title="Cookies & traceurs">
                            <p>
                                Le site utilise un cookie technique nécessaire à son bon fonctionnement (gestion de session).
                                Un outil d'analyse d'audience anonymisée peut être utilisé afin d'améliorer l'expérience utilisateur.
                                Aucune publicité ni reciblage ne sont mis en œuvre sur ce site.
                            </p>
                            <p>
                                Vous pouvez à tout moment refuser ou supprimer les cookies via les paramètres de votre navigateur.
                            </p>
                        </Section>

                        <Section id="droits" title="Vos droits">
                            <p>
                                Conformément au RGPD, vous disposez des droits suivants sur vos données personnelles :
                            </p>
                            <ul className="space-y-2 list-none pl-0">
                                {[
                                    ["Droit d'accès", "obtenir confirmation que vos données sont traitées et en obtenir copie."],
                                    ["Droit de rectification", "corriger toute donnée inexacte ou incomplète."],
                                    ["Droit à l'effacement", "demander la suppression de vos données (« droit à l'oubli »)."],
                                    ["Droit à la limitation", "demander le gel temporaire du traitement."],
                                    ["Droit d'opposition", "vous opposer à un traitement, notamment à des fins de prospection."],
                                    ["Droit à la portabilité", "récupérer vos données dans un format structuré."],
                                    ["Droit de retirer votre consentement", "à tout moment et sans justification."],
                                ].map(([t, d], i) => (
                                    <li key={i} className="flex gap-3 border-l-2 border-[#E63946] pl-4 py-1">
                                        <div>
                                            <span className="font-display font-bold uppercase text-sm tracking-tight">{t}</span>
                                            <span className="text-zinc-700"> — {d}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                            <p>
                                Pour exercer vos droits, contactez Atelier Beauchamp au <a href="tel:+33649501843" className="font-semibold underline underline-offset-2 hover:text-[#E63946]">06 49 50 18 43</a>
                                {" "}ou via le <Link to="/contact" className="font-semibold underline underline-offset-2 hover:text-[#E63946]">formulaire de contact</Link>.
                                Une réponse vous sera apportée dans un délai maximum d'un mois.
                            </p>
                            <p>
                                Vous disposez également du droit d'introduire une réclamation auprès de la <strong>CNIL</strong>
                                (Commission Nationale de l'Informatique et des Libertés) — <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="font-semibold underline underline-offset-2 hover:text-[#E63946]">www.cnil.fr</a>.
                            </p>
                        </Section>

                        <Section id="contact" title="Contact">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <a href="tel:+33649501843" data-testid="mentions-contact-call" className="border border-black/10 bg-white p-5 sm:p-6 hover:border-black transition-colors flex items-start gap-4">
                                    <div className="h-10 w-10 grid place-items-center bg-[#E63946] text-white flex-shrink-0">
                                        <Phone className="h-4 w-4" strokeWidth={2.4} />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">Téléphone</div>
                                        <div className="font-display font-bold mt-1 text-lg">06 49 50 18 43</div>
                                    </div>
                                </a>
                                <Link to="/contact" data-testid="mentions-contact-form" className="border border-black/10 bg-white p-5 sm:p-6 hover:border-black transition-colors flex items-start gap-4">
                                    <div className="h-10 w-10 grid place-items-center bg-[#0A0A0A] text-white flex-shrink-0">
                                        <Mail className="h-4 w-4" strokeWidth={2.4} />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-zinc-500">Demande écrite</div>
                                        <div className="font-display font-bold mt-1 text-lg">Formulaire en ligne</div>
                                    </div>
                                </Link>
                            </div>
                            <p className="mt-6 text-xs text-zinc-500 flex items-center gap-2">
                                <Lock className="h-3.5 w-3.5" /> Vos échanges sont traités confidentiellement.
                            </p>
                        </Section>
                    </div>
                </div>
            </section>
        </>
    );
};

export default MentionsLegales;
